import { api } from './api';

export interface WorkspaceReport {
  workspaceId: string;
  ai: {
    last7Days: { date: string; cents: number; runs: number }[];
    totalCents30d: number;
    runs30d: number;
    byModel: { model: string; runs: number; cents: number }[];
  };
  topQueries: { question: string; count: number }[];
  ingestion: {
    documents: { total: number; indexed: number; failed: number; pending: number };
    chunks: number;
  };
  connectors: {
    kind: string;
    status: string;
    lastSync: string | null;
    itemCount: number | null;
    lastMode: 'full' | 'incremental';
    nextSync: string | null;
    error: string | null;
  }[];
}

export interface PersonRow {
  userId: string;
  email: string | null;
  name: string | null;
  pictureUrl: string | null;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  scimProvisioned: boolean;
  isSiteAdmin: boolean;
}

export interface NextAction {
  kind: 'review_approvals' | 'retry_failed' | 'add_docs' | 'fix_connector' | 'ask_first' | 'explore';
  count?: number;
  to: string;
  priority: number;
}
export interface AnswerQuality {
  windowDays: number;
  totalAnswers: number;
  groundedRate: number | null;
  noEvidenceRate: number | null;
  avgCitations: number;
  feedback: { helpful: number; unhelpful: number; helpfulRate: number | null };
}

export interface NextActionsResult {
  stats: {
    docsTotal: number; docsIndexed: number; docsFailed: number;
    aiRuns: number; pendingApprovals: number; connectorErrors: number;
  };
  actions: NextAction[];
}

export const workspaceExtras = {
  async reports(workspaceId: string): Promise<WorkspaceReport> {
    const r = await api.get<WorkspaceReport>(`/api/workspaces/${workspaceId}/reports`);
    return r.data;
  },
  async nextActions(workspaceId: string): Promise<NextActionsResult> {
    const r = await api.get<NextActionsResult>(`/api/workspaces/${workspaceId}/next-actions`);
    return r.data;
  },
  async quality(workspaceId: string): Promise<AnswerQuality> {
    const r = await api.get<AnswerQuality>(`/api/workspaces/${workspaceId}/quality`);
    return r.data;
  },
  async people(workspaceId: string): Promise<PersonRow[]> {
    const r = await api.get<PersonRow[]>(`/api/workspaces/${workspaceId}/people`);
    return r.data;
  },
  async invitePerson(
    workspaceId: string,
    input: { email: string; name?: string; role?: 'admin' | 'member' },
  ): Promise<{ userId: string; role: 'admin' | 'member' }> {
    const r = await api.post<{ userId: string; role: 'admin' | 'member' }>(
      `/api/workspaces/${workspaceId}/people`,
      input,
    );
    return r.data;
  },
  async setPersonRole(workspaceId: string, userId: string, role: 'admin' | 'member'): Promise<void> {
    await api.patch(`/api/workspaces/${workspaceId}/people/${userId}`, { role });
  },
  async removePerson(workspaceId: string, userId: string): Promise<void> {
    await api.delete(`/api/workspaces/${workspaceId}/people/${userId}`);
  },
};
